import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  createPolicyDraft,
  getPolicies,
  getPolicyDraft,
  simulateRules,
  updatePolicyDraft,
  type PolicyDraft,
  type PolicyDraftInput,
  type PolicySummary,
  type RuleActionInput,
  type RuleConditionInput,
  type RuleSimulationResult,
  type SimulationContext,
} from '../api';
import {
  searchCommerceReferences,
  type CommerceReference,
  type ReferenceKind,
} from './referenceApi';

type PolicyBuilderProps = {
  storeCurrency: string | null;
};

type ScopeField =
  | 'all'
  | 'company_id'
  | 'company_location_id'
  | 'catalog_id'
  | 'product_id'
  | 'variant_id';

type PriceKind = 'percent_discount' | 'fixed_price' | 'percent_markup';

const scopeLabels: Record<ScopeField, string> = {
  all: 'All B2B buyers',
  company_id: 'One company',
  company_location_id: 'One company location',
  catalog_id: 'One catalog',
  product_id: 'One product',
  variant_id: 'One variant',
};

const scopeReferenceKinds: Record<Exclude<ScopeField, 'all'>, ReferenceKind> = {
  company_id: 'company',
  company_location_id: 'company_location',
  catalog_id: 'catalog',
  product_id: 'product',
  variant_id: 'variant',
};

function eventValue(event: Event): string {
  return (event.currentTarget as HTMLElement & { value: string }).value;
}

function optionalPositiveInteger(value: string, label: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    throw new Error(`${label} must be a positive whole number.`);
  }
  return parsed;
}

function positiveDecimal(value: string, label: string): string {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error(`${label} must be zero or greater.`);
  }
  return value.trim();
}

function referenceLabel(reference: CommerceReference): string {
  return reference.detail ? `${reference.label} — ${reference.detail}` : reference.label;
}

export function PolicyBuilder({ storeCurrency }: PolicyBuilderProps) {
  const currency = (storeCurrency || 'USD').toUpperCase();
  const [policies, setPolicies] = useState<PolicySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingPolicyKey, setEditingPolicyKey] = useState<string | null>(null);
  const [editingVersion, setEditingVersion] = useState(1);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('100');
  const [scopeField, setScopeField] = useState<ScopeField>('all');
  const [scopeValue, setScopeValue] = useState('');
  const [scopeSearch, setScopeSearch] = useState('');
  const [scopeReferences, setScopeReferences] = useState<CommerceReference[]>([]);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [priceKind, setPriceKind] = useState<PriceKind>('percent_discount');
  const [priceValue, setPriceValue] = useState('10');
  const [minimum, setMinimum] = useState('');
  const [maximum, setMaximum] = useState('');
  const [increment, setIncrement] = useState('');
  const [testQuantity, setTestQuantity] = useState('1');
  const [testBasePrice, setTestBasePrice] = useState('100');
  const [simulation, setSimulation] = useState<RuleSimulationResult | null>(null);

  const refreshPolicies = useCallback(async () => {
    const rows = await getPolicies();
    setPolicies(rows);
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    refreshPolicies()
      .catch((reason: unknown) => {
        if (!active) return;
        setError(reason instanceof Error ? reason.message : 'Could not load B2B policies.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [refreshPolicies]);

  useEffect(() => {
    if (scopeField === 'all') {
      setScopeReferences([]);
      setScopeLoading(false);
      setScopeError(null);
      return undefined;
    }

    const kind = scopeReferenceKinds[scopeField];
    let active = true;
    const timer = window.setTimeout(() => {
      setScopeLoading(true);
      setScopeError(null);
      void searchCommerceReferences(kind, scopeSearch)
        .then((references) => {
          if (!active) return;
          setScopeReferences(references);
          if (scopeValue && !references.some((item) => item.id === scopeValue)) {
            setScopeReferences((current) => [
              ...current,
              {
                id: scopeValue,
                label: 'Current saved selection',
                detail: scopeValue,
              },
            ]);
          }
        })
        .catch((reason: unknown) => {
          if (!active) return;
          setScopeError(
            reason instanceof Error ? reason.message : 'Could not search Shopify resources.',
          );
        })
        .finally(() => {
          if (active) setScopeLoading(false);
        });
    }, 300);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [scopeField, scopeSearch, scopeValue]);

  const changeScopeField = useCallback((next: ScopeField) => {
    setScopeField(next);
    setScopeValue('');
    setScopeSearch('');
    setScopeReferences([]);
    setScopeError(null);
    setSimulation(null);
  }, []);

  const reset = useCallback(() => {
    setEditingId(null);
    setEditingPolicyKey(null);
    setEditingVersion(1);
    setName('');
    setDescription('');
    setPriority('100');
    setScopeField('all');
    setScopeValue('');
    setScopeSearch('');
    setScopeReferences([]);
    setScopeError(null);
    setPriceKind('percent_discount');
    setPriceValue('10');
    setMinimum('');
    setMaximum('');
    setIncrement('');
    setSimulation(null);
    setError(null);
    setNotice(null);
  }, []);

  const draftInput = useMemo((): PolicyDraftInput | null => {
    try {
      if (!name.trim()) return null;
      const priorityNumber = Number(priority);
      if (!Number.isInteger(priorityNumber) || priorityNumber < 0 || priorityNumber > 1_000_000) {
        return null;
      }
      if (scopeField !== 'all' && !scopeValue.trim()) return null;

      const conditions: RuleConditionInput[] = [];
      if (scopeField !== 'all') {
        conditions.push({
          field: scopeField,
          operator: 'eq',
          value: scopeValue.trim(),
          sequence: 0,
        });
      }

      const actions: RuleActionInput[] = [];
      const commercialValue = positiveDecimal(
        priceValue,
        priceKind === 'fixed_price' ? 'Fixed price' : 'Percentage',
      );
      if (priceKind === 'fixed_price') {
        actions.push({
          kind: priceKind,
          value: { amount: commercialValue, currency },
          sequence: actions.length,
        });
      } else {
        actions.push({
          kind: priceKind,
          value: { percent: commercialValue },
          sequence: actions.length,
        });
      }

      const minValue = optionalPositiveInteger(minimum, 'Minimum quantity');
      const maxValue = optionalPositiveInteger(maximum, 'Maximum quantity');
      const incrementValue = optionalPositiveInteger(increment, 'Quantity increment');
      if (minValue !== null) {
        actions.push({
          kind: 'min_quantity',
          value: { quantity: minValue },
          sequence: actions.length,
        });
      }
      if (maxValue !== null) {
        actions.push({
          kind: 'max_quantity',
          value: { quantity: maxValue },
          sequence: actions.length,
        });
      }
      if (incrementValue !== null) {
        actions.push({
          kind: 'quantity_increment',
          value: { quantity: incrementValue },
          sequence: actions.length,
        });
      }

      return {
        name: name.trim(),
        description: description.trim() || null,
        priority: priorityNumber,
        conditions,
        actions,
      };
    } catch {
      return null;
    }
  }, [
    currency,
    description,
    increment,
    maximum,
    minimum,
    name,
    priceKind,
    priceValue,
    priority,
    scopeField,
    scopeValue,
  ]);

  const validateForm = useCallback((): PolicyDraftInput => {
    if (!name.trim()) throw new Error('Give this rule a name.');
    if (scopeField !== 'all' && !scopeValue.trim()) {
      throw new Error(`Select ${scopeLabels[scopeField].toLowerCase()} from Shopify.`);
    }
    if (!draftInput) {
      throw new Error('Review the priority, price and quantity values before continuing.');
    }
    return draftInput;
  }, [draftInput, name, scopeField, scopeValue]);

  const save = useCallback(async () => {
    setError(null);
    setNotice(null);
    setSaving(true);
    try {
      const input = validateForm();
      const result = editingId
        ? await updatePolicyDraft(editingId, input)
        : await createPolicyDraft(input);
      setEditingId(result.id);
      setEditingPolicyKey(result.policy_key);
      setEditingVersion(result.draft_version);
      setNotice(`Draft v${result.draft_version} saved. Nothing has been published to Shopify.`);
      await refreshPolicies();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not save policy draft.');
    } finally {
      setSaving(false);
    }
  }, [editingId, refreshPolicies, validateForm]);

  const loadForEdit = useCallback(async (policy: PolicySummary) => {
    setError(null);
    setNotice(null);
    setSimulation(null);
    try {
      const draft: PolicyDraft = await getPolicyDraft(policy.id);
      if (draft.conditions.length > 1) {
        throw new Error(
          'This draft uses advanced conditions and cannot be flattened by the simple editor.',
        );
      }
      const condition = draft.conditions[0];
      if (condition && (condition.operator !== 'eq' || !(condition.field in scopeLabels))) {
        throw new Error(
          'This draft uses an advanced condition and cannot be edited in the simple builder.',
        );
      }
      const priceActions = draft.actions.filter((item) =>
        ['percent_discount', 'percent_markup', 'fixed_price'].includes(item.kind),
      );
      if (priceActions.length !== 1) {
        throw new Error('This draft does not contain exactly one supported price action.');
      }
      const supportedActions = new Set([
        'percent_discount',
        'percent_markup',
        'fixed_price',
        'min_quantity',
        'max_quantity',
        'quantity_increment',
      ]);
      if (draft.actions.some((item) => !supportedActions.has(item.kind))) {
        throw new Error('This draft contains advanced actions and cannot be edited here safely.');
      }
      const priceAction = priceActions[0];

      setEditingId(draft.id);
      setEditingPolicyKey(draft.policy_key);
      setEditingVersion(draft.draft_version);
      setName(draft.name);
      setDescription(draft.description ?? '');
      setPriority(String(draft.priority));
      const nextScope = (condition?.field as ScopeField | undefined) ?? 'all';
      setScopeField(nextScope);
      setScopeValue(condition ? String(condition.value ?? '') : '');
      setScopeSearch(condition ? String(condition.value ?? '') : '');
      setScopeReferences([]);
      setScopeError(null);
      setPriceKind(priceAction.kind as PriceKind);
      setPriceValue(
        String(
          priceAction.kind === 'fixed_price'
            ? priceAction.value.amount ?? ''
            : priceAction.value.percent ?? '',
        ),
      );
      setMinimum(
        String(draft.actions.find((item) => item.kind === 'min_quantity')?.value.quantity ?? ''),
      );
      setMaximum(
        String(draft.actions.find((item) => item.kind === 'max_quantity')?.value.quantity ?? ''),
      );
      setIncrement(
        String(
          draft.actions.find((item) => item.kind === 'quantity_increment')?.value.quantity ?? '',
        ),
      );
      setNotice(`Editing ${draft.name} draft v${draft.draft_version}.`);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not open policy draft.');
    }
  }, []);

  const simulate = useCallback(async () => {
    setError(null);
    setNotice(null);
    setSimulating(true);
    setSimulation(null);
    try {
      const input = validateForm();
      const quantity = optionalPositiveInteger(testQuantity, 'Test quantity');
      if (quantity === null) throw new Error('Enter a test quantity.');
      const basePrice = positiveDecimal(testBasePrice, 'Test base price');
      const context: SimulationContext = {
        quantity,
        currency,
        base_price: basePrice,
      };
      if (scopeField !== 'all') context[scopeField] = scopeValue.trim();

      const result = await simulateRules(context, [
        {
          ...input,
          policy_key: editingPolicyKey ?? 'preview-policy',
          version_number: editingVersion,
        },
      ]);
      setSimulation(result);
      setNotice('Simulation completed. No Shopify data was changed.');
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not simulate this rule.');
    } finally {
      setSimulating(false);
    }
  }, [
    currency,
    editingPolicyKey,
    editingVersion,
    scopeField,
    scopeValue,
    testBasePrice,
    testQuantity,
    validateForm,
  ]);

  return (
    <s-section heading="B2B Commerce — Pricing & buying rules">
      <s-stack direction="block" gap="base">
        <s-paragraph>
          Build and test commercial rules as drafts. Publishing is intentionally disabled until
          Atellius can run store-wide impact analysis and conflict checks.
        </s-paragraph>

        <s-heading>{editingId ? 'Edit draft rule' : 'Create a draft rule'}</s-heading>
        <s-text-field
          label="Rule name"
          value={name}
          placeholder="Gold dealers — 20% off"
          onInput={(event) => setName(eventValue(event))}
        />
        <s-text-field
          label="Description"
          value={description}
          placeholder="Optional note for your team"
          onInput={(event) => setDescription(eventValue(event))}
        />
        <s-number-field
          label="Priority"
          value={priority}
          min={0}
          max={1000000}
          step={1}
          onInput={(event) => setPriority(eventValue(event))}
        />
        <s-select
          label="Applies to"
          value={scopeField}
          onChange={(event) => changeScopeField(eventValue(event) as ScopeField)}
        >
          <s-option value="all">All B2B buyers</s-option>
          <s-option value="company_id">One company</s-option>
          <s-option value="company_location_id">One company location</s-option>
          <s-option value="catalog_id">One catalog</s-option>
          <s-option value="product_id">One product</s-option>
          <s-option value="variant_id">One variant</s-option>
        </s-select>
        {scopeField !== 'all' && (
          <s-stack direction="block" gap="small">
            <s-text-field
              label={`Search ${scopeLabels[scopeField].toLowerCase()}`}
              value={scopeSearch}
              placeholder="Type a name, title, SKU, or Shopify ID"
              onInput={(event) => setScopeSearch(eventValue(event))}
            />
            <s-select
              label="Select from this Shopify store"
              value={scopeValue}
              onChange={(event) => setScopeValue(eventValue(event))}
              disabled={scopeLoading}
            >
              <s-option value="">
                {scopeLoading ? 'Searching…' : 'Choose a matching Shopify record'}
              </s-option>
              {scopeReferences.map((reference) => (
                <s-option key={reference.id} value={reference.id}>
                  {referenceLabel(reference)}
                </s-option>
              ))}
            </s-select>
            {scopeValue && (
              <s-paragraph>
                Selected: {referenceLabel(
                  scopeReferences.find((item) => item.id === scopeValue) ?? {
                    id: scopeValue,
                    label: scopeValue,
                    detail: null,
                  },
                )}
              </s-paragraph>
            )}
            {!scopeLoading && scopeReferences.length === 0 && !scopeError && (
              <s-paragraph>No matching Shopify records were found in the latest sync.</s-paragraph>
            )}
            {scopeError && <s-paragraph>Resource search needs attention: {scopeError}</s-paragraph>}
          </s-stack>
        )}

        <s-select
          label="Price rule"
          value={priceKind}
          onChange={(event) => setPriceKind(eventValue(event) as PriceKind)}
        >
          <s-option value="percent_discount">Percentage discount</s-option>
          <s-option value="fixed_price">Fixed price</s-option>
          <s-option value="percent_markup">Percentage markup</s-option>
        </s-select>
        <s-number-field
          label={priceKind === 'fixed_price' ? `Price (${currency})` : 'Percentage'}
          value={priceValue}
          min={0}
          step={0.01}
          onInput={(event) => setPriceValue(eventValue(event))}
        />

        <s-heading>Optional quantity rules</s-heading>
        <s-number-field
          label="Minimum quantity"
          value={minimum}
          min={1}
          step={1}
          onInput={(event) => setMinimum(eventValue(event))}
        />
        <s-number-field
          label="Maximum quantity"
          value={maximum}
          min={1}
          step={1}
          onInput={(event) => setMaximum(eventValue(event))}
        />
        <s-number-field
          label="Order in multiples of"
          value={increment}
          min={1}
          step={1}
          onInput={(event) => setIncrement(eventValue(event))}
        />

        <s-stack direction="inline" gap="small">
          <s-button onClick={() => void save()} disabled={saving || !draftInput}>
            {saving ? 'Saving…' : editingId ? 'Save draft changes' : 'Save draft'}
          </s-button>
          <s-button onClick={reset} disabled={saving || simulating}>
            New rule
          </s-button>
        </s-stack>

        <s-heading>Test this rule</s-heading>
        <s-number-field
          label="Test base price"
          value={testBasePrice}
          min={0}
          step={0.01}
          onInput={(event) => setTestBasePrice(eventValue(event))}
        />
        <s-number-field
          label="Test quantity"
          value={testQuantity}
          min={1}
          step={1}
          onInput={(event) => setTestQuantity(eventValue(event))}
        />
        <s-button onClick={() => void simulate()} disabled={simulating || !draftInput}>
          {simulating ? 'Simulating…' : 'Test rule — no changes'}
        </s-button>

        {simulation && (
          <s-stack direction="block" gap="small">
            <s-heading>
              {simulation.currency} {simulation.base_price} → {simulation.currency}{' '}
              {simulation.final_price}
            </s-heading>
            <s-paragraph>
              {simulation.safe_to_publish
                ? 'No conflict was detected in this test context.'
                : 'Conflict detected in this test context. This draft is not safe to publish.'}
            </s-paragraph>
            {simulation.min_quantity !== null && (
              <s-paragraph>Minimum quantity: {simulation.min_quantity}</s-paragraph>
            )}
            {simulation.max_quantity !== null && (
              <s-paragraph>Maximum quantity: {simulation.max_quantity}</s-paragraph>
            )}
            {simulation.quantity_increment !== null && (
              <s-paragraph>Quantity increment: {simulation.quantity_increment}</s-paragraph>
            )}
            {simulation.conflicts.map((conflict) => (
              <s-paragraph key={`${conflict.family}-${conflict.priority}`}>
                {conflict.message}
              </s-paragraph>
            ))}
          </s-stack>
        )}

        {notice && <s-paragraph>{notice}</s-paragraph>}
        {error && <s-paragraph>Needs attention: {error}</s-paragraph>}

        <s-heading>Saved policies</s-heading>
        {loading && <s-paragraph>Loading policy drafts…</s-paragraph>}
        {!loading && policies.length === 0 && (
          <s-paragraph>No B2B pricing rules have been saved yet.</s-paragraph>
        )}
        {policies.map((policy) => (
          <s-stack key={policy.id} direction="block" gap="small">
            <s-heading>{policy.name}</s-heading>
            <s-paragraph>
              Priority {policy.priority} · Draft{' '}
              {policy.draft_version ? `v${policy.draft_version}` : 'none'} · Published{' '}
              {policy.published_version ? `v${policy.published_version}` : 'none'}
            </s-paragraph>
            {policy.description && <s-paragraph>{policy.description}</s-paragraph>}
            {policy.draft_version && (
              <s-button onClick={() => void loadForEdit(policy)}>Edit draft</s-button>
            )}
          </s-stack>
        ))}
      </s-stack>
    </s-section>
  );
}
